import { useCallback, useRef, useState } from 'react';
import type {
  CreateIssueReportRequest,
  CreateIssueReportResponse,
  IssueQuickTag,
  IssueReportContext,
  IssueType,
} from '@ucp/web/lib';
import { NotSignedInError, useApiFetch } from './useApiFetch';

/**
 * Report an Issue submit hook (PRO-0550). Builds the `POST /issues` request from
 * the dialog's field values, auto-captures the non-PII technical `context`
 * (pathname only — the server strips any query/hash, SF-SEC-001), generates a
 * client `idempotencyKey` (crypto.randomUUID → safe retry, API-4), and surfaces a
 * simple `idle | submitting | success | error` status. On success it returns the
 * `ISS-…` reference for the confirmation view.
 *
 * The hook NEVER persists free-text client-side (privacy). The `context` is
 * captured at submit time so it reflects the route the reporter was on.
 */

export interface ReportIssueFields {
  readonly issueType: IssueType;
  readonly tried: string;
  readonly happened: string;
  readonly quickTags: readonly IssueQuickTag[];
  readonly extraDetails: string;
}

export type ReportIssueState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'success'; reference: string }
  | { status: 'error'; message: string };

function captureContext(appVersion: string): IssueReportContext {
  return {
    routePath: typeof window !== 'undefined' ? window.location.pathname : '',
    appVersion,
    viewport:
      typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

function messageForStatus(status: number, apiMessage?: string): string {
  switch (status) {
    case 400:
      return apiMessage ?? 'Please check the form and try again.';
    case 429:
      return 'You have sent several reports recently — please try again shortly.';
    case 503:
      return 'Issue reporting is temporarily unavailable. Please try again later.';
    default:
      return 'Sorry, your report could not be sent. Please try again.';
  }
}

export interface UseReportIssue {
  state: ReportIssueState;
  submit: (fields: ReportIssueFields) => Promise<void>;
  reset: () => void;
}

/** `appVersion` is injected (Vite `import.meta.env`) so context carries a build id. */
const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unknown';

export function useReportIssue(): UseReportIssue {
  const apiFetch = useApiFetch();
  const [state, setState] = useState<ReportIssueState>({ status: 'idle' });
  // Guards against a double-submit from a fast second click before the first
  // request settles (the idempotencyKey backstops the server side).
  const inFlight = useRef(false);

  const reset = useCallback(() => {
    inFlight.current = false;
    setState({ status: 'idle' });
  }, []);

  const submit = useCallback(
    async (fields: ReportIssueFields) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      setState({ status: 'submitting' });

      const body: CreateIssueReportRequest = {
        issueType: fields.issueType,
        tried: fields.tried,
        happened: fields.happened,
        quickTags: fields.quickTags,
        ...(fields.extraDetails.trim() ? { extraDetails: fields.extraDetails } : {}),
        context: captureContext(APP_VERSION),
        idempotencyKey: crypto.randomUUID(),
      };

      try {
        const response = await apiFetch('/issues', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        if (!response) {
          setState({ status: 'error', message: 'Re-authentication required. Please try again.' });
          return;
        }
        if (!response.ok) {
          let apiMessage: string | undefined;
          try {
            const err = (await response.json()) as { message?: string };
            apiMessage = typeof err.message === 'string' ? err.message : undefined;
          } catch {
            apiMessage = undefined;
          }
          setState({ status: 'error', message: messageForStatus(response.status, apiMessage) });
          return;
        }
        const data = (await response.json()) as CreateIssueReportResponse;
        setState({ status: 'success', reference: data.reference });
      } catch (error) {
        const message =
          error instanceof NotSignedInError
            ? error.message
            : 'Sorry, your report could not be sent. Please try again.';
        setState({ status: 'error', message });
      } finally {
        inFlight.current = false;
      }
    },
    [apiFetch],
  );

  return { state, submit, reset };
}
